import React, { useState, useEffect } from 'react';
import ActivityLogCard from './ActivityLogCard';
import ActivityFilters from './ActivityFilters';
import './ActivityTimeline.css';

const ActivityTimeline = ({ documentId, documentType, showFilters = true, maxItems = 50 }) => {
  const [logs, setLogs] = useState([]);
  const [filteredLogs, setFilteredLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [summary, setSummary] = useState(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: maxItems
  });
  const [filters, setFilters] = useState({
    user_id: '',
    action_type: '',
    start_date: '',
    end_date: '',
    search: ''
  });

  useEffect(() => {
    if (documentId) {
      fetchDocumentLogs();
      fetchDocumentSummary();
    } else {
      fetchAllLogs();
    }
  }, [documentId, documentType]);

  useEffect(() => {
    applyFilters();
  }, [logs, filters]);

  const fetchDocumentLogs = async (page = 1) => {
    setLoading(true);
    setError(null);
    try {
      const offset = (page - 1) * pagination.itemsPerPage;
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/activity-logs/document/${documentId}?limit=${pagination.itemsPerPage}&offset=${offset}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setLogs(data.logs || data);
        
        // Update pagination if API returns pagination info
        if (data.total !== undefined) {
          setPagination(prev => ({
            ...prev,
            currentPage: page,
            totalItems: data.total,
            totalPages: Math.ceil(data.total / prev.itemsPerPage)
          }));
        }
      } else {
        setError('Erreur lors du chargement de l\'historique');
      }
    } catch (err) {
      setError('Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  const fetchDocumentSummary = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/activity-logs/summary/${documentId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setSummary(data);
      }
    } catch (err) {
      console.error('Erreur lors du chargement du résumé:', err);
    }
  };

  const fetchAllLogs = async (page = 1) => {
    setLoading(true);
    setError(null);
    try {
      const offset = (page - 1) * pagination.itemsPerPage;
      const params = new URLSearchParams({
        limit: pagination.itemsPerPage.toString(),
        offset: offset.toString()
      });
      
      if (documentType) {
        params.append('document_type', documentType);
      }

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/activity-logs?${params}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setLogs(data.logs || data);
        
        // Update pagination if API returns pagination info
        if (data.total !== undefined) {
          setPagination(prev => ({
            ...prev,
            currentPage: page,
            totalItems: data.total,
            totalPages: Math.ceil(data.total / prev.itemsPerPage)
          }));
        }
      } else {
        setError('Erreur lors du chargement de l\'historique');
      }
    } catch (err) {
      setError('Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...logs];

    // Filtre par utilisateur
    if (filters.user_id) {
      filtered = filtered.filter(log => log.user_id === filters.user_id);
    }

    // Filtre par type d'action
    if (filters.action_type) {
      filtered = filtered.filter(log => log.action_type === filters.action_type);
    }

    // Filtre par date de début
    if (filters.start_date) {
      const startDate = new Date(filters.start_date);
      filtered = filtered.filter(log => new Date(log.timestamp) >= startDate);
    }

    // Filtre par date de fin
    if (filters.end_date) {
      const endDate = new Date(filters.end_date);
      filtered = filtered.filter(log => new Date(log.timestamp) <= endDate);
    }

    // Recherche textuelle
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter(log => 
        log.user_name.toLowerCase().includes(searchLower) ||
        log.document_reference?.toLowerCase().includes(searchLower) ||
        JSON.stringify(log.metadata).toLowerCase().includes(searchLower)
      );
    }

    setFilteredLogs(filtered);
  };

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
  };

  const getUniqueUsers = () => {
    const users = logs.map(log => ({ id: log.user_id, name: log.user_name }));
    const uniqueUsers = users.filter((user, index, self) => 
      index === self.findIndex(u => u.id === user.id)
    );
    return uniqueUsers;
  };

  const exportToCsv = async () => {
    try {
      const params = new URLSearchParams();
      if (documentId) params.append('document_id', documentId);
      if (filters.user_id) params.append('user_id', filters.user_id);
      if (filters.action_type) params.append('action_type', filters.action_type);
      if (filters.start_date) params.append('start_date', filters.start_date);
      if (filters.end_date) params.append('end_date', filters.end_date);

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/activity-logs/export/csv?${params}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `activity_logs_${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      } else {
        setError('Erreur lors de l\'export CSV');
      }
    } catch (err) {
      setError('Erreur lors de l\'export CSV');
    }
  };

  const exportToPdf = async () => {
    try {
      const params = new URLSearchParams();
      if (documentId) params.append('document_id', documentId);
      if (filters.user_id) params.append('user_id', filters.user_id);
      if (filters.action_type) params.append('action_type', filters.action_type);
      if (filters.start_date) params.append('start_date', filters.start_date);
      if (filters.end_date) params.append('end_date', filters.end_date);

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/activity-logs/export/pdf?${params}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `activity_logs_${new Date().toISOString().split('T')[0]}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      } else {
        setError('Erreur lors de l\'export PDF');
      }
    } catch (err) {
      setError('Erreur lors de l\'export PDF');
    }
  };

  // Pagination functions
  const handlePageChange = (page) => {
    if (page >= 1 && page <= pagination.totalPages) {
      if (documentId) {
        fetchDocumentLogs(page);
      } else {
        fetchAllLogs(page);
      }
    }
  };

  const handlePreviousPage = () => {
    if (pagination.currentPage > 1) {
      handlePageChange(pagination.currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (pagination.currentPage < pagination.totalPages) {
      handlePageChange(pagination.currentPage + 1);
    }
  };

  const renderPagination = () => {
    if (pagination.totalPages <= 1) return null;

    const pages = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, pagination.currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(pagination.totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    return (
      <div className="pagination-controls">
        <div className="pagination-info">
          <span>
            Page {pagination.currentPage} sur {pagination.totalPages} 
            ({pagination.totalItems} éléments au total)
          </span>
        </div>
        <div className="pagination-buttons">
          <button 
            onClick={handlePreviousPage}
            disabled={pagination.currentPage === 1}
            className="pagination-button"
          >
            ← Précédent
          </button>
          
          {pages.map(page => (
            <button
              key={page}
              onClick={() => handlePageChange(page)}
              className={`pagination-button ${page === pagination.currentPage ? 'active' : ''}`}
            >
              {page}
            </button>
          ))}
          
          <button 
            onClick={handleNextPage}
            disabled={pagination.currentPage === pagination.totalPages}
            className="pagination-button"
          >
            Suivant →
          </button>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="activity-timeline-loading">
        <div className="spinner"></div>
        <p>Chargement de l'historique...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="activity-timeline-error">
        <div className="error-icon"></div>
        <p>{error}</p>
        <button onClick={() => window.location.reload()} className="retry-button">
          Réessayer
        </button>
      </div>
    );
  }

  return (
    <div className="activity-timeline">
      {/* Header avec résumé */}
      {summary && (
        <div className="activity-timeline-header">
          <div className="timeline-summary">
            <h3>📊 Résumé d'activité</h3>
            <div className="summary-stats">
              <div className="stat">
                <span className="stat-value">{summary.total_actions}</span>
                <span className="stat-label">Actions</span>
              </div>
              <div className="stat">
                <span className="stat-value">{summary.users_involved?.length || 0}</span>
                <span className="stat-label">Utilisateurs</span>
              </div>
              <div className="stat">
                <span className="stat-value">{summary.last_activity ? new Date(summary.last_activity).toLocaleDateString() : '-'}</span>
                <span className="stat-label">Dernière activité</span>
              </div>
            </div>
          </div>
          <div className="timeline-actions">
            <button onClick={exportToCsv} className="export-button csv">
              📊 Export CSV
            </button>
            <button onClick={exportToPdf} className="export-button pdf">
              📄 Export PDF
            </button>
          </div>
        </div>
      )}

      {/* Filtres */}
      {showFilters && (
        <ActivityFilters
          filters={filters}
          onFilterChange={handleFilterChange}
          users={getUniqueUsers()}
        />
      )}

      {/* Timeline */}
      <div className="timeline-container">
        <div className="timeline-header">
          <h3>📋 Historique d'activité</h3>
          <span className="timeline-count">
            {filteredLogs.length} {filteredLogs.length === 1 ? 'action' : 'actions'}
          </span>
        </div>

        {filteredLogs.length === 0 ? (
          <div className="timeline-empty">
            <div className="empty-icon">📝</div>
            <p>Aucune activité trouvée</p>
            <small>Les actions effectuées apparaîtront ici</small>
          </div>
        ) : (
          <div className="timeline-content">
            <div className="timeline-line"></div>
            {filteredLogs.map((log, index) => (
              <ActivityLogCard
                key={log.id}
                log={log}
                isFirst={index === 0}
                isLast={index === filteredLogs.length - 1}
              />
            ))}
          </div>
        )}
        
        {/* Pagination Controls */}
        {renderPagination()}
      </div>
    </div>
  );
};

export default ActivityTimeline;